import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ReservationModel } from 'src/app/models/reservation.model';
import { ReservationService } from 'src/app/services/reservation.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { getSpanishPaginatorIntl } from 'src/app/shared/services/paginator.service';
import { DocumentTypeLabelPipe } from 'src/app/shared/pipes/enum-label.pipe';
import { ReservationDetailDialogComponent } from '../reservation-detail-modal/reservation-detail-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { RoomModel } from 'src/app/models/room.model';
import { RoomService } from 'src/app/services/room.service';
import { DescriptionService } from 'src/app/shared/services/description.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReservationStatusEnum } from 'src/app/shared/enumerators/reservation-status.enum';
import { Router } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-booking-list',
  templateUrl: './booking-list.component.html',
  styleUrl: './booking-list.component.css',
  standalone: true,
  providers: [
    { provide: MatPaginatorIntl, useValue: getSpanishPaginatorIntl() }
  ],
  imports: [
    MatTableModule, MatButtonModule, MatIconModule, MatPaginatorModule,
    MatSortModule, CommonModule, MatLabel, MatFormField, MatInputModule, MatDatepickerModule,
    MatNativeDateModule, DocumentTypeLabelPipe, MatTooltipModule,
    MatSelectModule, MatMenuModule
  ]
})
export class BookingListComponent implements OnInit, AfterViewInit {

  reservations: ReservationModel[] = [];
  displayedColumns: string[] = [
    'responsible',
    'document',
    'checkInDate',
    'checkOutDate',
    'id',
    'status',
    'actions'
  ];

  filterCheckIn: Date | null = null;
  filterCheckOut: Date | null = null;
  statusFilter: string = '';
  pageSize = 10;
  currentPage = 0;
  totalCount = 0;
  sortField: string = 'checkInDate';
  sortDirection: string = 'asc';
  filterText = '';
  dataSource = new MatTableDataSource<ReservationModel>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  rooms: RoomModel[] = [];

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.sort.sortChange.subscribe(sort => {
      this.sortField = sort.active;
      this.sortDirection = sort.direction;
      this.paginator.firstPage(); // Reinicia paginador si querés
      this.fetchData();
    });
    this.paginatorIntl.changes.next();
    this.fetchData();
  }

  constructor(private reservationSvc: ReservationService,
    private toastr: ToastService,
    private paginatorIntl: MatPaginatorIntl,
    private dialog: MatDialog,
    private roomSrv: RoomService,
    public descriptionService: DescriptionService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.getRooms()
  }

  getReservations() {
    this.reservationSvc.get().subscribe({
      next: (data) => {
        this.dataSource.data = data;
      },
      error: (err) => {
        this.toastr.error(
          'Reservas',
          'Error al obtener las reservas'
        );
      }
    });
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.fetchData();
  }

  applyFilter(event: Event) {
    this.filterText = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.paginator.firstPage();
    this.fetchData();
  }

  onCheckInFilterChange(event: MatDatepickerInputEvent<Date>) {
    this.filterCheckIn = event.value;
    this.paginator.firstPage();
    this.fetchData();
  }

  onCheckOutFilterChange(event: MatDatepickerInputEvent<Date>) {
    this.filterCheckOut = event.value;
    this.paginator.firstPage();
    this.fetchData();
  }

  onStatusChange(event: MatDatepickerInputEvent<string>) {
    this.statusFilter = event.value;
    this.paginator.firstPage();
    this.fetchData();
  }

  clearFilters(textInput: HTMLInputElement) {
    this.filterText = '';
    this.filterCheckIn = null;
    this.filterCheckOut = null;
    this.statusFilter = '';
    textInput.value = '';
    this.paginator.firstPage();
    this.fetchData();
  }

  fetchData() {
    const skip = this.currentPage * this.pageSize;
    const top = this.pageSize;

    const filters: string[] = [];

    if (this.filterText) {
      const escaped = this.filterText.replace(/'/g, "''");
      var texts = [
        `contains(tolower(bookingPerson/firstName),'${escaped}')`,
        `contains(tolower(bookingPerson/lastName),'${escaped}')`,
        `contains(tolower(bookingPerson/documentNumber),'${escaped}')`,
      ]
      filters.push(`(${texts.join(' or ')})`);
    }

    if (this.statusFilter != '') {
      filters.push(`status eq '${this.statusFilter}'`)
    }

    let orderByClause = '';
    if (this.sortField && this.sortDirection) {
      switch (this.sortField) {
        case "responsible":
          orderByClause = `$orderby=bookingPerson/firstName ${this.sortDirection},bookingPerson/lastName ${this.sortDirection}`;
          break;
        case "document":
          orderByClause = `$orderby=bookingPerson/documentType ${this.sortDirection},bookingPerson/documentNumber ${this.sortDirection}`;
          break;
        default:
          orderByClause = `$orderby=${this.sortField} ${this.sortDirection}`;
      }
    }

    const today = new Date();
    const todayStr = today.toISOString();
    if (this.filterCheckIn && this.filterCheckOut) {
      // Ambos filtros
      filters.push(`checkInDate ge ${this.filterCheckIn.toISOString()}`);
      filters.push(`checkOutDate le ${this.filterCheckOut.toISOString()}`);
    } else if (this.filterCheckIn) {
      // Solo check-in
      filters.push(`checkInDate ge ${this.filterCheckIn.toISOString()}`);
    } else if (this.filterCheckOut) {
      // Solo check-out
      filters.push(`checkOutDate le ${this.filterCheckOut.toISOString()}`);
    } else if (filters.length <= 0) {
      // Ninguno, default: mostrar desde hoy en adelante
      filters.push(`checkOutDate ge ${todayStr}`);
    }

    const filterClause = filters.length > 0 ? `$filter=${filters.join(' and ')}` : '';
    const paging = `$skip=${skip}&$top=${top}&$count=true`;
    const odataQuery = [filterClause, orderByClause, paging].filter(p => !!p).join('&');

    this.reservationSvc.query(odataQuery).subscribe({
      next: (data: any) => {
        this.dataSource.data = data.items;
        this.totalCount = data.totalCount || 0;
      },
      error: (err) => {
        this.toastr.error(
          'Reservas',
          'Error al obtener las reservas'
        );
      }
    });
  }

  getRooms() {
    this.roomSrv.get().subscribe({
      next: (data) => {
        this.rooms = data;
      },
      error: (err) => {
        this.toastr.error(
          'Habitaciones',
          'Error al obtener habitaciones'
        );
      }
    });
  }

  viewReservation(reservation: ReservationModel) {

    reservation.reservationDetails.forEach(rd => {
      rd.room = this.getRoomById(rd.roomId);
    });

    this.dialog.open(ReservationDetailDialogComponent, {
      data: reservation,
      width: '700px'
    });
  }

  getRoomById(roomId: number): RoomModel {
    return this.rooms.find(r => r.id === roomId);
  }

  editReservation(reservation: ReservationModel) {

    this.router.navigate([`booking`], { queryParams: { id: reservation.id } });
  }

  checkIn(reservation: ReservationModel) {

    this.router.navigate([`check-in`], { queryParams: { id: reservation.id } });
    // this.updateStatus(reservation, ReservationStatusEnum.CheckedIn, 'El Check-in se inicio correctamente', 'Error al iniciar el Check-in');
  }

  checkOut(reservation: ReservationModel) {
    this.updateStatus(reservation, ReservationStatusEnum.CheckedOut, 'El Check-out se realizó correctamente', 'Error al reslizar el Check-out');
  }

  cancel(reservation: ReservationModel) {
    this.updateStatus(reservation, ReservationStatusEnum.Cancelled, 'La reserva se canceló correctamente', 'Error al realizar la cancelación');
  }

  payment(reservation: ReservationModel) {
    this.router.navigate([`payment`], { queryParams: { id: reservation.id } });
  }

  updateStatus(reservation: ReservationModel, status: ReservationStatusEnum, messageOk: string, messageError: string) {
    this.reservationSvc.updateStatus(reservation.id, status).subscribe({
      next: (data) => {
        if (data == true) {
          this.updateStatusInList(reservation, status);
          this.toastr.success('Reserva', messageOk);
        } else {
          this.toastr.error('Reserva', messageError);
        }
      },
      error: (err) => {
        this.toastr.error('Reserva', messageError);
      }
    });
  }

  canCheckIn(reservation: ReservationModel): boolean {
    const today = new Date();
    const checkIn = new Date(reservation.checkInDate ?? '');
    return reservation.status === ReservationStatusEnum.Reserved &&
      checkIn.toDateString() === today.toDateString();
  }

  canCheckOut(reservation: ReservationModel): boolean {
    return reservation.status === ReservationStatusEnum.CheckedIn;
  }

  canCancel(reservation: ReservationModel): boolean {
    return reservation.status === ReservationStatusEnum.Reserved || reservation.status === ReservationStatusEnum.Pending;
  }

  canEdit(reservation: ReservationModel): boolean {
    return reservation.status === ReservationStatusEnum.Reserved
      || reservation.status === ReservationStatusEnum.Pending
      || reservation.status === ReservationStatusEnum.CheckedIn;
  }

  canRegisterPayment(reservation: ReservationModel): boolean {
    let totalAmount = reservation?.payments?.reduce((sum, p) => sum + (p.amount ?? 0), 0) || 0;
    return reservation.totalPrice > totalAmount
      && (reservation.status === ReservationStatusEnum.Pending
        || reservation.status === ReservationStatusEnum.Reserved);
  }

  updateStatusInList(reservation: ReservationModel, status: ReservationStatusEnum) {
    reservation.status = status;
    const index = this.dataSource.data.findIndex(r => r.id === reservation.id);
    if (index !== -1) {
      this.dataSource.data[index] = reservation;
      this.dataSource.data = [...this.dataSource.data];
    }
  }

  configurePaginator() {
    const dutchRangeLabel = (
      page: number,
      pageSize: number,
      length: number
    ) => {
      if (length == 0 || pageSize == 0) {
        return `0 de ${length}`;
      }

      length = Math.max(length, 0);

      const startIndex = page * pageSize;

      const endIndex =
        startIndex < length
          ? Math.min(startIndex + pageSize, length)
          : startIndex + pageSize;

      return `${startIndex + 1} - ${endIndex} de ${length}`;
    };

    this.dataSource.sort = this.sort;
    this.paginator._intl.itemsPerPageLabel = '';
    this.paginator._intl.firstPageLabel = 'Primera página';
    this.paginator._intl.lastPageLabel = 'Última página';
    this.paginator._intl.nextPageLabel = 'Siguente página';
    this.paginator._intl.previousPageLabel = 'Página anterior';
    this.paginator._intl.getRangeLabel = dutchRangeLabel;
    this.dataSource.paginator = this.paginator;
  }
}
